<template>
  <v-dialog v-model="dialog" persistent max-width="720px">
    <v-card class="payee-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Edit Payee</div>
          <div class="dialog-subtitle">
            Update payee contact and address details.
          </div>
        </div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="form-section">
              <div class="section-title">Name & Notes</div>

              <div class="field-label">Name</div>
              <v-text-field
                outlined
                dense
                :hide-details="nameErrors.length === 0"
                :error-messages="nameErrors"
                v-model="Payee.name"
                label="Name"
              />

              <div class="field-label mt-3">Phone Number</div>
              <v-text-field
                outlined
                dense
                hide-details
                v-model="Payee.phoneNumber"
                label="Phone Number"
                prepend-inner-icon="mdi-phone-outline"
              />

              <div class="field-label mt-3">Email Address</div>
              <v-text-field
                outlined
                dense
                hide-details
                v-model="Payee.email"
                label="Email Address"
                prepend-inner-icon="mdi-email-outline"
              />

              <div class="field-label mt-3">Description</div>
              <v-textarea
                outlined
                dense
                hide-details
                auto-grow
                rows="3"
                v-model="Payee.note"
                :label="$t('labels.description')"
              />
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="form-section">
              <div class="section-title">Address</div>

              <div class="field-label">Search Address</div>
              <GoogleAddressInput
                label="Search Address"
                @address-selected="setAddress"
              />

              <div class="field-label mt-3">Address</div>
              <v-text-field
                outlined
                hide-details
                dense
                v-model="Payee.address"
                label="Address"
              />

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">City</div>
                  <v-text-field
                    outlined
                    hide-details
                    dense
                    v-model="Payee.city"
                    label="City"
                  />
                </v-col>

                <v-col cols="12" sm="3">
                  <div class="field-label mt-3">State</div>
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    v-model="Payee.state"
                    label="State"
                  />
                </v-col>

                <v-col cols="12" sm="3">
                  <div class="field-label mt-3">Zip</div>
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    v-model="Payee.zip"
                    label="Zip"
                  />
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />

        <v-btn color="primary" text @click="close">
          Close
        </v-btn>

        <v-btn
          color="primary"
          depressed
          rounded
          :loading="loading"
          dark
          @click="submit"
        >
          Update Payee
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import payeeService from "../service.js";
import GoogleAddressInput from "@/modules/Google/GoogleAddressInput.vue";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "update-payee",

  props: {
    editDialog: {
      default: false,
    },
    editPayee: {
      type: Object,
      default: null,
    },
  },

  components: {
    GoogleAddressInput,
  },

  mixins: [validationMixin],

  data() {
    return {
      loading: false,
      dialog: false,
      Payee: this.getEmptyPayee(),
    };
  },

  validations: {
    Payee: {
      name: {
        required,
      },
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    nameErrors() {
      const errors = [];

      if (!this.$v.Payee.name.$dirty) {
        return errors;
      }

      if (!this.$v.Payee.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      return errors;
    },
  },

  watch: {
    editDialog(val) {
      this.dialog = val;

      if (val) {
        this.Payee = this.editPayee
          ? JSON.parse(JSON.stringify(this.editPayee))
          : this.getEmptyPayee();

        this.$v.$reset();
      }
    },
  },

  methods: {
    getEmptyPayee() {
      return {
        id: null,
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      };
    },

    setAddress(addressData) {
      this.Payee.address = addressData.address;
      this.Payee.city = addressData.city;
      this.Payee.state = addressData.state;
      this.Payee.zip = addressData.zip;
    },

    close() {
      this.empty();
      this.dialog = false;
      this.$emit("close");
    },

    empty() {
      this.Payee = this.getEmptyPayee();
      this.$v.$reset();
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (!this.Payee.id) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Payee ID is missing",
          });

        return;
      }

      this.loading = true;

      return payeeService
        .update(this.Payee.id, this.Payee)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Payee is updated",
              });

            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          const message =
            err && err.data && err.data.message
              ? err.data.message
              : "Unable to update payee";

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: message,
            });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style scoped>
.payee-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  height: 100%;
}

.section-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.dialog-actions {
  padding: 12px 16px;
}

@media (max-width: 600px) {
  .dialog-header {
    padding: 14px 16px;
  }

  .dialog-title {
    font-size: 18px;
  }

  .form-section {
    padding: 12px;
    height: auto;
  }
}
</style>